import { useState, useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import axios from "axios";
import { ZoomIn, ZoomOut, RotateCcw, Filter, Info, X } from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { TOPOLOGY } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NODE_CONFIG = {
  cluster:    { r: 22, color: "#6366f1", stroke: "#818cf8", label: "CLUSTER" },
  namespace:  { r: 16, color: "#8b5cf6", stroke: "#a78bfa", label: "NS" },
  deployment: { r: 13, color: "#3b82f6", stroke: "#60a5fa", label: "DEPLOY" },
  pod:        { r: 8,  color: "#22c55e", stroke: "#4ade80", label: "POD" },
  service:    { r: 10, color: "#f59e0b", stroke: "#fbbf24", label: "SVC" },
};

const FILTERS = ["all", "cluster", "namespace", "deployment", "pod", "service"];

function NodeDetail({ node, onClose }) {
  if (!node) return null;
  const cfg = NODE_CONFIG[node.type] || NODE_CONFIG.deployment;
  const statusColor = node.status === "Running" || node.status === "active" || node.status === "Active" || node.status === "healthy"
    ? "text-emerald-400" : node.status === "CrashLoopBackOff" ? "text-rose-400" : "text-amber-400";
  return (
    <div className="absolute top-4 right-4 w-64 bg-slate-900/95 border border-slate-700 rounded-md p-4 shadow-xl backdrop-blur-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.color }} />
          <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: cfg.color }}>{node.type}</span>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X size={13} /></button>
      </div>
      <p className="text-sm font-semibold text-slate-100 mb-2 font-mono truncate">{node.name}</p>
      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between"><span className="text-slate-500">Status</span><span className={statusColor}>{node.status}</span></div>
        {node.replicas !== undefined && (
          <div className="flex justify-between"><span className="text-slate-500">Replicas</span><span className="text-slate-300 font-mono">{node.ready_replicas}/{node.replicas}</span></div>
        )}
        {node.pods !== undefined && (
          <div className="flex justify-between"><span className="text-slate-500">Pods</span><span className="text-slate-300 font-mono">{node.pods}</span></div>
        )}
      </div>
    </div>
  );
}

export default function Topology() {
  const svgRef = useRef(null);
  const { clusterId } = useCluster();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedNode, setSelectedNode] = useState(null);
  const zoomRef = useRef(null);
  const svgSelection = useRef(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/clusters/${clusterId}/topology`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clusterId]);

  const resetView = useCallback(() => {
    if (svgSelection.current && zoomRef.current) {
      svgSelection.current.transition().duration(600).call(zoomRef.current.transform, d3.zoomIdentity);
    }
  }, []);

  const handleZoom = useCallback((delta) => {
    if (svgSelection.current && zoomRef.current) {
      svgSelection.current.transition().duration(300).call(zoomRef.current.scaleBy, delta);
    }
  }, []);

  useEffect(() => {
    if (!data || !svgRef.current) return;

    const filteredNodes = filter === "all" ? data.nodes : data.nodes.filter((n) => n.type === filter || n.type === "cluster");
    const nodeIds = new Set(filteredNodes.map((n) => n.id));
    const filteredLinks = data.links.filter((l) => {
      const src = typeof l.source === "object" ? l.source.id : l.source;
      const tgt = typeof l.target === "object" ? l.target.id : l.target;
      return nodeIds.has(src) && nodeIds.has(tgt);
    });

    const nodes = filteredNodes.map((n) => ({ ...n }));
    const links = filteredLinks.map((l) => ({ ...l }));

    const el = svgRef.current;
    const w = el.clientWidth || 900;
    const h = el.clientHeight || 600;

    d3.select(el).selectAll("*").remove();

    const svg = d3.select(el);
    svgSelection.current = svg;

    const zoom = d3.zoom().scaleExtent([0.1, 4]).on("zoom", (e) => g.attr("transform", e.transform));
    zoomRef.current = zoom;
    svg.call(zoom);

    const g = svg.append("g");

    // Defs for arrow marker
    svg.append("defs").append("marker")
      .attr("id", "arrow").attr("viewBox", "0 -5 10 10")
      .attr("refX", 20).attr("markerWidth", 4).attr("markerHeight", 4).attr("orient", "auto")
      .append("path").attr("d", "M0,-5L10,0L0,5").attr("fill", "#334155");

    const sim = d3.forceSimulation(nodes)
      .force("link", d3.forceLink(links).id((d) => d.id).distance((l) => {
        const types = [l.source.type || "pod", l.target.type || "pod"];
        if (types.includes("cluster")) return 180;
        if (types.includes("namespace")) return 120;
        if (types.includes("deployment")) return 80;
        return 55;
      }).strength(0.6))
      .force("charge", d3.forceManyBody().strength((d) => d.type === "cluster" ? -600 : d.type === "namespace" ? -300 : -120))
      .force("center", d3.forceCenter(w / 2, h / 2))
      .force("collision", d3.forceCollide((d) => (NODE_CONFIG[d.type]?.r || 10) + 14));

    const link = g.append("g").selectAll("line").data(links).join("line")
      .attr("stroke", "#1e293b").attr("stroke-width", 1.2)
      .attr("stroke-dasharray", (d) => d.type === "routes_to" ? "4,3" : null);

    const node = g.append("g").selectAll("circle").data(nodes).join("circle")
      .attr("r", (d) => NODE_CONFIG[d.type]?.r || 10)
      .attr("fill", (d) => {
        if (d.type === "pod") return d.status === "Running" ? "#22c55e" : d.status === "CrashLoopBackOff" ? "#f87171" : "#fbbf24";
        return NODE_CONFIG[d.type]?.color || "#64748b";
      })
      .attr("stroke", (d) => NODE_CONFIG[d.type]?.stroke || "#94a3b8")
      .attr("stroke-width", 1.5)
      .attr("fill-opacity", 0.9)
      .style("cursor", "pointer")
      .call(d3.drag()
        .on("start", (e, d) => { if (!e.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
        .on("drag", (e, d) => { d.fx = e.x; d.fy = e.y; })
        .on("end", (e, d) => { if (!e.active) sim.alphaTarget(0); d.fx = null; d.fy = null; }))
      .on("click", (_, d) => setSelectedNode(d));

    node.append("title").text((d) => `${d.type}: ${d.name}\nStatus: ${d.status}`);

    const label = g.append("g").selectAll("text").data(nodes).join("text")
      .text((d) => d.name.length > 16 ? d.name.slice(0, 14) + "…" : d.name)
      .attr("font-size", (d) => d.type === "cluster" ? 11 : d.type === "namespace" ? 10 : 9)
      .attr("fill", "#94a3b8")
      .attr("text-anchor", "middle")
      .attr("dy", (d) => (NODE_CONFIG[d.type]?.r || 10) + 13)
      .style("pointer-events", "none");

    sim.on("tick", () => {
      link.attr("x1", (d) => d.source.x).attr("y1", (d) => d.source.y)
          .attr("x2", (d) => d.target.x).attr("y2", (d) => d.target.y);
      node.attr("cx", (d) => d.x).attr("cy", (d) => d.y);
      label.attr("x", (d) => d.x).attr("y", (d) => d.y);
    });

    return () => sim.stop();
  }, [data, filter]);

  return (
    <div data-testid={TOPOLOGY.page} className="flex flex-col h-full space-y-4" style={{ height: "calc(100vh - 120px)" }}>
      {/* Controls */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div data-testid={TOPOLOGY.filterBar} className="flex items-center gap-1 bg-slate-900/50 border border-slate-800 rounded-md p-1">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={cn("px-2.5 py-1 rounded text-xs font-medium transition-colors capitalize",
                filter === f ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "text-slate-400 hover:text-slate-200")}>
              {f}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={TOPOLOGY.zoomIn} onClick={() => handleZoom(1.3)} className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"><ZoomIn size={14} /></button>
          <button data-testid={TOPOLOGY.zoomOut} onClick={() => handleZoom(0.77)} className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"><ZoomOut size={14} /></button>
          <button data-testid={TOPOLOGY.resetView} onClick={resetView} className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"><RotateCcw size={14} /></button>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative flex-1 bg-slate-900/30 border border-slate-800 rounded-md overflow-hidden">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2 border-slate-700 border-t-indigo-400 animate-spin" />
          </div>
        )}
        <svg
          ref={svgRef}
          data-testid={TOPOLOGY.canvas}
          className="w-full h-full"
          style={{ background: "transparent" }}
        />
        <NodeDetail node={selectedNode} onClose={() => setSelectedNode(null)} />

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-slate-900/90 border border-slate-800 rounded-md p-3 text-[10px]">
          <p className="text-slate-500 font-semibold uppercase tracking-wider mb-2">Legend</p>
          {Object.entries(NODE_CONFIG).map(([type, cfg]) => (
            <div key={type} className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.color }} />
              <span className="text-slate-400 capitalize">{type}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 mt-1 pt-1 border-t border-slate-800">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-rose-400" />
            <span className="text-slate-400">Failed pod</span>
          </div>
        </div>
      </div>
    </div>
  );
}
